import React from 'react';
import PropTypes from 'prop-types';
import { CloseOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Row, Col, Button, Alert } from 'antd';
import modal from '~components/modal';
import * as queries from '~settings/queries';
import { fetch } from '~helpers/graphql';
import PhoneNumber from '~components/phone-number';
import { validateEmojiRegex, validateEmail } from 'common/helpers/validate';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { landlordContactType } from '~constants/landlord';
import get from 'lodash/get';

@modal(
  {
    className: 'property-contact-modal',
  },
  true,
)
class PropertyContactModal extends React.Component {
  constructor(props) {
    super(props);

    const { currentContact } = props;
    const { contactName, contactEmail, contactPhone, occupation } = currentContact || {};

    this.state = {
      contactName: contactName || '',
      contactEmail: contactEmail || '',
      contactPhone: contactPhone || '',
      occupation: occupation || '',

      isNormalEmailOrPhone: true,
      isPhoneMLimit: true,
      isEmailValid: true,

      phoneErrorMsg: null,
      emailErrorMsg: null,
    };

    this.loading = false;
  }

  handleClickConfirm = () => {
    const { type, currentContact } = this.props;
    const { contactEmail, contactPhone, isEmailValid } = this.state;
    if (this.loading) {
      return false;
    }

    this.props.form.validateFieldsAndScroll(
      // eslint-disable-next-line consistent-return
      err => {
        if (this.props.contactType === landlordContactType.FINANCE_TEAM) {
          if (!contactEmail) {
            this.setState({
              isEmailValid: false,
              emailErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
            });
            return false;
          }
        } else {
          if (!contactEmail && !contactPhone) {
            this.setState({
              isNormalEmailOrPhone: false,
              phoneErrorMsg: this.props.t(
                'cms.landlord.contact.modal.error.contact_email_phone.limit',
              ),
              emailErrorMsg: this.props.t(
                'cms.landlord.contact.modal.error.contact_email_phone.limit',
              ),
            });
            return false;
          }
        }

        if (contactEmail && !isEmailValid) {
          this.setState({
            isEmailValid: false,
            emailErrorMsg: this.props.t(
              'cms.landlord.contact.modal.error.contact_email_format.error',
            ),
          });
          return false;
        }

        if (!this.validatePhoneAndEmail()) {
          return false;
        }
        console.log(err);
        if (!err) {
          const params = this.props.form.getFieldsValue();
          params.contactPhone = this.state.contactPhone;
          params.contactEmail = this.state.contactEmail;

          Object.keys(params).map(key => {
            if (typeof params[key] === 'string') {
              params[key] = params[key].trim();
            }
            return true;
          });

          this.loading = true;

          if (!params.contactEmail) {
            params.contactEmail = null;
          }

          if (!params.contactPhone) {
            params.contactPhone = null;
          }

          if (!params.occupation) {
            params.occupation = null;
          }

          if (type === 'create') {
            this.handleSubmit(params);
          }

          if (type === 'edit') {
            params.id = currentContact.id;
            this.handleSubmit(params);
          }
        }
      },
    );
    return true;
  };

  handleSubmit = contact => {
    try {
      const { contactType, property, otherContacts = [] } = this.props;

      if ([landlordContactType.PROPERTY, landlordContactType.FINANCE_TEAM].includes(contactType)) {
        fetch(
          queries.bindPropertyContacts({
            propertyId: property.id,
            propertyContacts: [{ ...contact, type: contactType }].concat(otherContacts),
          }),
        )
          .then(response => {
            if (
              response &&
              response.bindPropertyContacts &&
              response.bindPropertyContacts.propertyContacts
            ) {
              this.props.handleCloseModal();
              this.props.handleOnSuccess();
            }
          })
          .catch(e => {
            console.log('error', e);
          })
          .finally(() => {
            this.loading = false;
          });
      }
      if (contactType === landlordContactType.BOOKING_TEAM) {
        fetch(
          queries.bindBookingTeamContacts({
            propertyId: property.id,
            bookingTeamContacts: [contact, ...otherContacts],
          }),
        )
          .then(response => {
            if (
              response &&
              response.bindBookingTeamContacts &&
              response.bindBookingTeamContacts.bookingTeamContacts
            ) {
              this.props.handleCloseModal();
              this.props.handleOnSuccess();
            }
          })
          .finally(() => {
            this.loading = false;
          });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  validatePhoneAndEmail = () => {
    const { contactPhone } = this.state;
    const params = this.props.form.getFieldsValue();
    if (
      this.props.contactType !== landlordContactType.FINANCE_TEAM &&
      params.contactEmail === '' &&
      contactPhone.trim().length <= 3
    ) {
      this.setState({
        isNormalEmailOrPhone: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
        emailErrorMsg: this.props.t('cms.landlord.contact.modal.error.blank'),
      });
      return false;
    }

    if (contactPhone.trim().length > 32) {
      this.setState({
        isPhoneMLimit: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.phone.limit'),
      });
      return false;
    }

    if (contactPhone.trim().length > 3 && !isValidPhoneNumber(contactPhone)) {
      this.setState({
        isPhoneMLimit: false,
        phoneErrorMsg: this.props.t('cms.landlord.contact.modal.error.contact_phone.invalid'),
      });
      return false;
    }
    this.setState({
      isNormalEmailOrPhone: true,
      isPhoneMLimit: true,
      phoneErrorMsg: null,
      emailErrorMsg: null,
    });
    return true;
  };

  handleValidateEmail = () => {
    const { t } = this.props;
    const { contactEmail } = this.state;
    if (contactEmail.length > 255) {
      this.setState({
        isEmailValid: false,
        emailErrorMsg: t('cms.landlord.contact.modal.error.contact_email.limit'),
      });
    } else if (!validateEmail(contactEmail)) {
      this.setState({
        isEmailValid: false,
        emailErrorMsg: t('cms.landlord.contact.modal.error.contact_email_format.error'),
      });
    } else {
      this.setState({
        isEmailValid: true,
        emailErrorMsg: null,
        isNormalEmailOrPhone: true,
        phoneErrorMsg: null,
      });
    }
  };

  handleInputEmailChange = e => {
    this.setState({
      contactEmail: e.trim(),
    });
  };

  handleContactPhoneChange = e => {
    if (e && e.trim().length > 4) {
      this.setState({ contactPhone: e });
    } else {
      this.setState({ contactPhone: '' });
    }
  };

  getPhoneValidateStatus = (isNormalEmailOrPhone, isPhoneMLimit) => {
    if (this.props.contactType !== landlordContactType.FINANCE_TEAM) {
      return isNormalEmailOrPhone && isPhoneMLimit ? null : 'error';
    } else {
      return isPhoneMLimit ? null : 'error';
    }
  };

  render() {
    const { t, type, form, property, contactType } = this.props;
    const { getFieldDecorator } = form;
    const {
      contactName,
      contactEmail,
      contactPhone,
      occupation,
      isNormalEmailOrPhone,
      isPhoneMLimit,
      phoneErrorMsg,
      emailErrorMsg,
    } = this.state;

    return (
      <div className="landlord-contact-info">
        <div className="landlord-contact-info__header">
          {t(
            `cms.landlord.contact.modal.${type}_${this.props.contactType.toLowerCase()}_contact.title`,
          )}
          <CloseOutlined
            className="landlord-contact-info__close-button"
            onClick={this.props.handleCloseModal}
          />
        </div>
        <div className="landlord-contact-info__body">
          <If condition={contactType === landlordContactType.BOOKING_TEAM}>
            <Alert
              className="landlord-contact-info__alert"
              showIcon
              type="warning"
              message={`${t('cms.landlord.contact.modal.booking_team.alert')}.`}
            ></Alert>
          </If>
          <Form className="landlord-contact-info__form">
            <section className="landlord-contact-info__section">
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_name.label')}:`}
                    colon={false}
                  >
                    {getFieldDecorator('contactName', {
                      rules: [
                        {
                          required: true,
                          message: t('cms.landlord.contact.modal.error.blank'),
                        },
                        {
                          max: 80,
                          message: t('cms.landlord.contact.modal.error.contact_name.limit'),
                        },
                        {
                          validator: (rule, value, callback) => {
                            if (!validateEmojiRegex(value.trim())) {
                              callback(
                                t('cms.landlord.contact.modal.error.contact_name.emoji.limit'),
                              );
                            } else {
                              callback();
                            }
                          },
                        },
                      ],
                      initialValue: contactName,
                      validateTrigger: 'onBlur',
                    })(<Input placeholder={t('cms.landlord.contact.contact_name.label')} />)}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_email.label')}:`}
                    colon={false}
                    validateStatus={emailErrorMsg ? 'error' : null}
                    help={emailErrorMsg}
                  >
                    <Input
                      placeholder={t('cms.landlord.contact.contact_email.label')}
                      value={contactEmail}
                      onChange={e => {
                        this.handleInputEmailChange(e.target.value);
                      }}
                      onBlur={this.handleValidateEmail}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_phone.label')}:`}
                    colon={false}
                    validateStatus={this.getPhoneValidateStatus(
                      isNormalEmailOrPhone,
                      isPhoneMLimit,
                    )}
                    help={phoneErrorMsg}
                  >
                    <PhoneNumber
                      placeholder={t('cms.properties.edit.others.phone_number')}
                      value={contactPhone}
                      onChange={e => {
                        this.handleContactPhoneChange(e);
                      }}
                      checkValid={() => {
                        this.validatePhoneAndEmail();
                      }}
                      t={t}
                      countrySlug={get(property, 'city.country.slug')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Form.Item
                    label={`${t('cms.landlord.contact.contact_occupation.label')}:`}
                    colon={false}
                  >
                    {getFieldDecorator('occupation', {
                      rules: [
                        {
                          max: 255,
                          message: t('cms.landlord.contact.modal.error.contact_email.limit'),
                        },
                      ],
                      initialValue: occupation,
                      validateTrigger: 'onBlur',
                    })(<Input placeholder={t('cms.landlord.contact.contact_occupation.label')} />)}
                  </Form.Item>
                </Col>
              </Row>
            </section>
          </Form>
        </div>

        <div className="landlord-contact-info__footer">
          <Button
            ghost
            type="primary"
            size="large"
            className="landlord-contact-info__btn landlord-contact-info__cancel-button"
            onClick={this.props.handleCloseModal}
          >
            {t('cms.landlord.modal.cancel.button')}
          </Button>
          <Button
            type="primary"
            size="large"
            className="landlord-contact-info__btn landlord-contact-info__confirm-button"
            onClick={this.handleClickConfirm}
            loading={this.loading}
          >
            {t('cms.landlord.modal.confirm.button')}
          </Button>
        </div>
      </div>
    );
  }
}

PropertyContactModal.propTypes = {
  t: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['create', 'edit']),
  contactType: PropTypes.oneOf([
    'LANDLORD',
    'BOOKING_TEAM',
    'PROPERTY',
    landlordContactType.FINANCE_TEAM,
  ]),
  handleCloseModal: PropTypes.func.isRequired,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
    getFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
  currentContact: PropTypes.object,
  otherContacts: PropTypes.array,
  property: PropTypes.object,
  handleOnSuccess: PropTypes.func,
};

PropertyContactModal.defaultProps = {
  t: () => {},
  type: 'create',
  contactType: 'LANDLORD',
  handleCloseModal: () => {},
  form: {
    getFieldDecorator: () => {},
    setFieldsValue: () => {},
    validateFieldsAndScroll: () => {},
    getFieldsValue: () => {},
  },
  currentContact: {},
  otherContacts: [],
  property: {},
  handleOnSuccess: () => {},
};

export default Form.create({
  name: 'cms_landlord_modal',
})(PropertyContactModal);
